/**
 * DashboardPage - Página de Dashboard com gráficos e estatísticas
 */
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNotification } from '@context/NotificationContext'
import { APP_CONFIG } from '@config/constants'
import { CHART_THEME, DATA_SERIES_COLORS, STATUS_SERIES_COLORS } from '@config/visualTheme'
import FiltersService from '@services/filters'
import ClassificationService from '@services/classification'
import ApiService from '@services/api'
import { isPrazoConcluido, normalizeStatus } from '@services/prazoStatus'
import StatCard from './StatCard'
import ChartComponent from './ChartComponent'

const PESO_AMARELO = 0.5
const PESO_VERMELHO = 1
const DEBUG_PRAZOS = import.meta.env.DEV
let closedPrazosRequest = null

function debugDashboardPrazos(...args) {
  if (DEBUG_PRAZOS) console.debug(...args)
}

function fetchClosedPrazos() {
  if (closedPrazosRequest) return closedPrazosRequest

  closedPrazosRequest = (async () => {
    const pageSize = 1000
    let page = 1
    let total = 0
    const allPrazos = []

    do {
      const filters = { limit: pageSize, page, statusScope: 'closed' }
      debugDashboardPrazos('Fetching prazos', { queryKey: ['prazos-performance', page], filters })
      const result = await ApiService.getPrazos(filters)
      if (!result.success) throw new Error(result.error || 'Não foi possível carregar os prazos concluídos.')

      const list = Array.isArray(result.items) ? result.items : []
      total = Number(result.data?.total) || list.length
      allPrazos.push(...list)
      page++

      if (!list.length) break
    } while (allPrazos.length < total)

    return allPrazos
  })().finally(() => {
    closedPrazosRequest = null
  })

  return closedPrazosRequest
}

function getConclusaoStatus(prazo) {
  const statusAtual = normalizeStatus(prazo.status)

  if (statusAtual === 'verde') return 'verde'
  if (statusAtual === 'laranja' || statusAtual === 'amarelo') return 'amarelo'
  if (statusAtual === 'vermelho') return 'vermelho'

  const statusRealizacao = normalizeStatus(prazo.statusRealizacao)
  if (statusRealizacao === 'no_prazo' || statusRealizacao === 'adiantado') return 'verde'
  if (statusRealizacao === 'atrasado') {
    const diasAtraso = Number(prazo.diasUteisAtraso || 0)
    return diasAtraso > 2 ? 'vermelho' : 'amarelo'
  }

  const dataRealizacao = prazo.dataRealizacao || prazo.concluidoEm
  const dataVencimento = prazo.data || prazo.dataVencimento
  if (dataRealizacao && dataVencimento) {
    const realizacao = new Date(dataRealizacao)
    const vencimento = new Date(dataVencimento)
    realizacao.setHours(0, 0, 0, 0)
    vencimento.setHours(0, 0, 0, 0)
    const diffDias = Math.ceil((realizacao - vencimento) / (1000 * 60 * 60 * 24))
    if (diffDias <= 0) return 'verde'
    if (diffDias <= 2) return 'amarelo'
    return 'vermelho'
  }

  return 'amarelo'
}

function calcularPerformancePonderada(totalConcluidos, amarelo, vermelho) {
  if (!totalConcluidos) return 0
  const penalidade = PESO_AMARELO * amarelo + PESO_VERMELHO * vermelho
  const percentual = 100 * (1 - penalidade / totalConcluidos)
  return Math.round(Math.max(0, Math.min(100, percentual)))
}

function getPerformanceDate(prazo) {
  const value = prazo.dataRealizacao || prazo.concluidoEm || prazo.updatedAt || prazo.updated_at
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function normalizePersonName(value) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

function getResponsibleName(prazo) {
  return normalizePersonName(prazo.responsavel || prazo.advogado)
}

function getPersonKey(value) {
  return normalizePersonName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

export default function DashboardPage() {
  const { showError, showSuccess } = useNotification()
  const [areaFilter, setAreaFilter] = useState('')
  const [periodFilter, setPeriodFilter] = useState('month')
  const [isSyncing, setIsSyncing] = useState(false)
  const [closedPrazos, setClosedPrazos] = useState([])
  const [closedPrazosLoading, setClosedPrazosLoading] = useState(false)

  const loadClosedPrazos = useCallback(async () => {
    setClosedPrazosLoading(true)

    try {
      setClosedPrazos(await fetchClosedPrazos())
    } catch (error) {
      setClosedPrazos([])
      showError(error.message || 'Não foi possível carregar os dados de performance.')
    } finally {
      setClosedPrazosLoading(false)
    }
  }, [showError])

  useEffect(() => {
    debugDashboardPrazos('Prazos query mounted', { queryKey: ['prazos-performance'] })
    return () => debugDashboardPrazos('Prazos query unmounted', { queryKey: ['prazos-performance'] })
  }, [])

  useEffect(() => {
    loadClosedPrazos()
  }, [loadClosedPrazos])

  const filteredPrazos = useMemo(() => {
    const { startDate, endDate } = FiltersService.calcularPeriodoDatas(periodFilter)
    return closedPrazos.filter((prazo) => {
      if (areaFilter && ClassificationService.resolveArea(prazo) !== areaFilter) return false
      if (!startDate || !endDate) return true
      const completionDate = getPerformanceDate(prazo)
      return completionDate && completionDate >= startDate && completionDate <= endDate
    })
  }, [closedPrazos, areaFilter, periodFilter])

  const performancePrazos = useMemo(() => {
    return filteredPrazos.filter((p) => {
      const tipoAgenda = (p.tipoAgenda || p.tipo_agenda || '').trim().toLowerCase()
      return tipoAgenda === 'prazo'
    })
  }, [filteredPrazos])

  const concluidosPerformancePrazos = useMemo(
    () => performancePrazos.filter((p) => isPrazoConcluido(p)),
    [performancePrazos]
  )

  // Statistics
  const stats = useMemo(() => {
    const total = concluidosPerformancePrazos.length

    let verde = 0
    let amarelo = 0
    let vermelho = 0

    concluidosPerformancePrazos.forEach((p) => {
      const statusConclusao = getConclusaoStatus(p)
      if (statusConclusao === 'vermelho') vermelho++
      else if (statusConclusao === 'amarelo') amarelo++
      else verde++
    })

    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) + '%' : '0%')

    const advSet = new Set()
    let assignedTotal = 0
    concluidosPerformancePrazos.forEach((p) => {
      const responsavel = getResponsibleName(p)
      if (responsavel) {
        advSet.add(getPersonKey(responsavel))
        assignedTotal++
      }
    })

    const performance = calcularPerformancePonderada(total, amarelo, vermelho)
    const prazosPorAdv = advSet.size > 0 ? Math.round((assignedTotal / advSet.size) * 10) / 10 : 0

    return { total, verde, amarelo, vermelho, concluido: total, pct, advogados: advSet.size, performance, prazosPorAdv }
  }, [concluidosPerformancePrazos])

  // Area distribution chart data
  const areaChartData = useMemo(() => {
    const areas = {}
    performancePrazos.forEach((p) => {
      const area = ClassificationService.resolveArea(p)
      areas[area] = (areas[area] || 0) + 1
    })
    return {
      labels: Object.keys(areas).map((a) => ClassificationService.getAreaName(a)),
      datasets: [
        {
          data: Object.values(areas),
          backgroundColor: [...DATA_SERIES_COLORS],
        },
      ],
    }
  }, [performancePrazos])

  // Performance chart data
  const performanceChartData = useMemo(() => {
    const monthly = {}
    concluidosPerformancePrazos.forEach((p) => {
      const date = getPerformanceDate(p)
      if (!date) return
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!monthly[key]) monthly[key] = { total: 0, verde: 0, amarelo: 0, vermelho: 0 }
      monthly[key].total++
      const statusConclusao = getConclusaoStatus(p)
      monthly[key][statusConclusao]++
    })
    const sorted = Object.keys(monthly).sort()
    return {
      labels: sorted.map((m) => {
        const [y, mo] = m.split('-')
        return `${mo}/${y}`
      }),
      datasets: [
        {
          label: 'Performance (%)',
          data: sorted.map((m) =>
            calcularPerformancePonderada(monthly[m].total, monthly[m].amarelo, monthly[m].vermelho)
          ),
          borderColor: CHART_THEME.linePrimary,
          backgroundColor: CHART_THEME.linePrimaryFill,
          tension: 0.4,
        },
        {
          label: 'Concluídos',
          data: sorted.map((m) => monthly[m].total),
          borderColor: CHART_THEME.lineSecondary,
          backgroundColor: CHART_THEME.lineSecondaryFill,
          tension: 0.4,
        },
      ],
    }
  }, [concluidosPerformancePrazos])

  // Status chart data
  const statusChartData = useMemo(
    () => ({
      labels: ['No Prazo', 'Em Atenção', 'Em Atraso'],
      datasets: [
        {
          label: 'Quantidade',
          data: [stats.verde, stats.amarelo, stats.vermelho],
          backgroundColor: [...STATUS_SERIES_COLORS],
        },
      ],
    }),
    [stats]
  )

  // Detailed stats by area
  const statsPorArea = useMemo(() => {
    const result = {}
    concluidosPerformancePrazos.forEach((p) => {
      const area = ClassificationService.getAreaName(ClassificationService.resolveArea(p))
      if (!result[area]) result[area] = { total: 0, noPrazo: 0, emAtencao: 0, vencidos: 0 }
      result[area].total++

      const statusConclusao = getConclusaoStatus(p)
      if (statusConclusao === 'vermelho') result[area].vencidos++
      else if (statusConclusao === 'amarelo') result[area].emAtencao++
      else result[area].noPrazo++
    })
    Object.keys(result).forEach((a) => {
      const s = result[a]
      s.performance = calcularPerformancePonderada(s.total, s.emAtencao, s.vencidos)
    })
    return result
  }, [concluidosPerformancePrazos])

  // Stats by advogado
  const statsPorAdvogado = useMemo(() => {
    const result = {}
    concluidosPerformancePrazos.forEach((p) => {
      const resp = getResponsibleName(p)
      const key = resp ? getPersonKey(resp) : '__unassigned__'
      if (!result[key]) result[key] = { nome: resp || 'Não atribuído', total: 0, noPrazo: 0, emAtencao: 0, vencidos: 0, papel: resp ? 'Responsável' : 'Sem responsável' }
      result[key].total++

      const statusConclusao = getConclusaoStatus(p)
      if (statusConclusao === 'vermelho') result[key].vencidos++
      else if (statusConclusao === 'amarelo') result[key].emAtencao++
      else result[key].noPrazo++
    })
    Object.keys(result).forEach((a) => {
      const s = result[a]
      s.performance = calcularPerformancePonderada(s.total, s.emAtencao, s.vencidos)
    })
    return result
  }, [concluidosPerformancePrazos])

  // Stats by tipo de agenda (campo vindo do backend)
  const statsPorTipoAgenda = useMemo(() => {
    const result = {}
    performancePrazos.forEach((p) => {
      const tipoAgenda = (p.tipoAgenda || p.tipo_agenda || '').trim() || 'Não informado'
      result[tipoAgenda] = (result[tipoAgenda] || 0) + 1
    })
    return Object.entries(result).sort(([, totalA], [, totalB]) => totalB - totalA)
  }, [performancePrazos])

  const handleRefresh = async () => {
    if (isSyncing) return

    setIsSyncing(true)

    try {
      const result = await ApiService.sincronizarLegalManager()

      if (!result.success) {
        showError(result.error || 'Não foi possível sincronizar com o Legal Manager.')
        return
      }

      await loadClosedPrazos()

      showSuccess('Sincronização concluída!')
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-actions">
          <div className="filter-group">
            <label>Área:</label>
            <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
              <option value="">Todas as áreas</option>
              {Object.entries(APP_CONFIG.AREAS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Período:</label>
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
              {Object.entries(APP_CONFIG.PERIODOS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary btn-page-action"
            onClick={handleRefresh}
            disabled={isSyncing}
          >
            <i className={`fas ${isSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} /> {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      </div>

      {(isSyncing || closedPrazosLoading) && (
        <div className="page-loading-overlay" role="status" aria-live="polite">
          <div className="loading-spinner">
            <div className="spinner" />
            <p>{isSyncing ? 'Sincronizando dados, aguarde...' : 'Carregando prazos concluídos...'}</p>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="stats-overview">
        <StatCard index="01" type="verde" icon="fa-check-circle" value={stats.verde} label="Concluídos no Prazo" percentage={stats.pct(stats.verde)} />
        <StatCard index="02" type="laranja" icon="fa-exclamation-triangle" value={stats.amarelo} label="Concluídos em Atenção" percentage={stats.pct(stats.amarelo)} />
        <StatCard index="03" type="vermelho" icon="fa-times-circle" value={stats.vermelho} label="Concluídos em Atraso" percentage={stats.pct(stats.vermelho)} />
        <StatCard index="04" type="concluido" icon="fa-trophy" value={stats.concluido} label="Total Concluídos" percentage={stats.pct(stats.concluido)} />
      </div>

      {/* Performance Summary */}
      <div className="performance-summary">
        <div className="summary-item">
          <div className="summary-number">{stats.advogados}</div>
          <div className="summary-label">Advogados</div>
        </div>
        <div className="summary-item">
          <div className="summary-number">{stats.performance}%</div>
          <div className="summary-label">Performance</div>
        </div>
        <div className="summary-item">
          <div className="summary-number">{stats.prazosPorAdv}</div>
          <div className="summary-label">Prazos/Advogado</div>
        </div>
      </div>

      {/* Stats Details */}
      <div className="stats-details">
        <div className="detailed-stats">
          {/* By Area */}
          <div className="stats-section">
            <h4><span className="section-index">05</span> Performance por Área</h4>
            {Object.entries(statsPorArea).map(([area, s]) => (
              <div key={area} className="stat-item">
                <div className="stat-header">
                  <span className="stat-area">{area}</span>
                  <span
                    className={`stat-performance ${
                      s.performance >= 80
                        ? 'performance-alta'
                        : s.performance >= 60
                        ? 'performance-media'
                        : 'performance-baixa'
                    }`}
                  >
                    {s.performance}%
                  </span>
                </div>
                <div className="stat-details">
                  <span className="stat-total">{s.total} prazos</span>
                  <span className="stat-breakdown">
                    <span className="sr-only">No prazo: {s.noPrazo}. Em atenção: {s.emAtencao}. Vencidos: {s.vencidos}.</span>
                    <span aria-hidden="true"><span className="status-dot is-success" />{s.noPrazo}</span>
                    <span aria-hidden="true"><span className="status-dot is-warning" />{s.emAtencao}</span>
                    <span aria-hidden="true"><span className="status-dot is-danger" />{s.vencidos}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* By Advogado */}
          <div className="stats-section">
            <h4><span className="section-index">06</span> Performance por Advogado</h4>
            {Object.entries(statsPorAdvogado)
              .sort(([, a], [, b]) => b.performance - a.performance)
              .map(([adv, s]) => (
                <div key={adv} className="stat-item">
                  <div className="stat-header">
                    <span className="stat-advogado">{s.nome}</span>
                    <span className="stat-papel">{s.papel}</span>
                    <span
                      className={`stat-performance ${
                        s.performance >= 80
                          ? 'performance-alta'
                          : s.performance >= 60
                          ? 'performance-media'
                          : 'performance-baixa'
                      }`}
                    >
                      {s.performance}%
                    </span>
                  </div>
                  <div className="stat-details">
                    <span className="stat-total">{s.total} prazos</span>
                    <span className="stat-breakdown">
                      <span className="sr-only">No prazo: {s.noPrazo}. Em atenção: {s.emAtencao}. Vencidos: {s.vencidos}.</span>
                      <span aria-hidden="true"><span className="status-dot is-success" />{s.noPrazo}</span>
                      <span aria-hidden="true"><span className="status-dot is-warning" />{s.emAtencao}</span>
                      <span aria-hidden="true"><span className="status-dot is-danger" />{s.vencidos}</span>
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <div className="chart-header">
            <h3>Distribuição por Área</h3>
          </div>
          <div className="chart-content" style={{ height: '300px' }}>
            <ChartComponent type="doughnut" data={areaChartData} title="Distribuição de Prazos por Área" />
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-header">
            <h3>Evolução Temporal</h3>
          </div>
          <div className="chart-content" style={{ height: '300px' }}>
            <ChartComponent type="line" data={performanceChartData} title="Evolução Temporal da Performance" />
          </div>
        </div>
        <div className="chart-container full-width">
          <div className="chart-header">
            <h3>Distribuição por Status</h3>
          </div>
          <div className="chart-content" style={{ height: '300px' }}>
            <ChartComponent type="bar" data={statusChartData} title="Distribuição por Status" />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-card">
          <h3>Resumo por Tipo de Agenda</h3>
          <div className="area-summary-grid">
            {statsPorTipoAgenda.map(([tipoAgenda, total]) => (
              <div key={tipoAgenda} className="metric-item">
                <span className="metric-label">{tipoAgenda}</span>
                <span className="metric-value">{total} processos</span>
              </div>
            ))}
          </div>
        </div>
        <div className="details-card">
          <h3>Resumo por Área</h3>
          <div className="area-summary-grid">
            {Object.entries(statsPorArea).map(([area, s]) => (
              <div key={area} className="metric-item">
                <span className="metric-label">{area}</span>
                <span className="metric-value">
                  {s.total} prazos ({s.performance}%)
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="details-card">
          <h3>Performance Geral</h3>
          <div className="performance-metrics">
            <div className="metric-item">
              <span className="metric-label">Total de Advogados:</span>
              <span className="metric-value">{stats.advogados}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Performance Média:</span>
              <span className="metric-value">{stats.performance}%</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Processos por Advogado:</span>
              <span className="metric-value">{stats.prazosPorAdv}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
